import { definedMap, panic } from "@glideapps/ts-necessities";
import { asMaybeNumber } from "../common/components/component-helpers";
import type { PrimitiveValue } from "../common/components/computation-types";
import { cleanNumber } from "../common/components/functions";
import type { GroundValue, LoadingValue } from "../common/computation-model/data";
import { isLoadingValue, isPrimitive } from "../common/computation-model/data";
import type { Formula, PrimitiveGlideTypeKind } from "../common/description";
import { FormulaKind, isDateOrDateTimeTypeKind } from "../common/description";
import type {
    BinaryMathFormula,
    ConstantFormula,
    GetVariableFormula,
    UnaryMathFormula,
    WithUserEnteredTextFormula,
} from "../common/formula";
import { BinaryMathFunction, UnaryMathFunction } from "../common/formula";
import { getWeekNumber, log2, modulo, round2, trunc2 } from "../common/math-functions";
import { convertGlideDurationToJS, convertJSDurationToGlide } from "../support";
import { DateTimeAsyncParser } from "./parse-date-time";

class UndefinedDate {}
const undefinedDate = new UndefinedDate();

export interface MathContext {
    // This must be usable as a first-class function
    setAllDirty: () => void;
    lookupVariable(n: string): GroundValue;
}

export interface MathEvaluator {
    eval(context: MathContext): GroundValue;
}

export class MathInterpreter extends DateTimeAsyncParser {
    constructor(
        protected readonly _formula: Formula,
        private readonly _variableTypes: ReadonlyMap<string, PrimitiveGlideTypeKind>
    ) {
        super();
    }

    public eval(context: MathContext): GroundValue {
        const compute = (formula: Formula): PrimitiveValue | UndefinedDate | LoadingValue => {
            switch (formula.kind) {
                case FormulaKind.Constant:
                    return (formula as ConstantFormula).value;
                case FormulaKind.Random:
                    return cleanNumber(Math.random());
                case FormulaKind.UnaryMath: {
                    const f = formula as UnaryMathFormula;
                    const operandValue = compute(f.operand);

                    // Handle date/time operations first.
                    if (operandValue instanceof UndefinedDate) return undefined;
                    if (isLoadingValue(operandValue)) return operandValue;
                    const date = this.parseDateTime(operandValue, context.setAllDirty);
                    if (date === undefined || isLoadingValue(date)) return date;

                    switch (f.fn) {
                        case UnaryMathFunction.Year:
                            return date.getUTCFullYear();
                        case UnaryMathFunction.Month:
                            return date.getUTCMonth() + 1;
                        case UnaryMathFunction.Day:
                            return date.getUTCDate();
                        case UnaryMathFunction.Hour:
                            return date.getUTCHours();
                        case UnaryMathFunction.Minute:
                            return date.getUTCMinutes();
                        case UnaryMathFunction.Second:
                            return date.getUTCSeconds();
                        case UnaryMathFunction.Weekday:
                            return date.getUTCDay() + 1;
                        case UnaryMathFunction.WeekNumber:
                            return getWeekNumber(date);
                        default:
                            break;
                    }

                    const operand = asMaybeNumber(operandValue);
                    if (operand === undefined) return undefined;
                    let result: number;
                    switch (f.fn) {
                        case UnaryMathFunction.Negate:
                            result = -operand;
                            break;
                        case UnaryMathFunction.Absolute:
                            result = Math.abs(operand);
                            break;
                        case UnaryMathFunction.Floor:
                            result = Math.floor(operand);
                            break;
                        case UnaryMathFunction.Ceiling:
                            result = Math.ceil(operand);
                            break;
                        case UnaryMathFunction.Round:
                            result = Math.round(operand);
                            break;
                        case UnaryMathFunction.Sign:
                            result = Math.sign(operand);
                            break;
                        case UnaryMathFunction.Truncate:
                            result = Math.trunc(operand);
                            break;
                        case UnaryMathFunction.Sine:
                            result = Math.sin(operand);
                            break;
                        case UnaryMathFunction.Cosine:
                            result = Math.cos(operand);
                            break;
                        case UnaryMathFunction.Tangent:
                            result = Math.tan(operand);
                            break;
                        case UnaryMathFunction.ArcSine:
                            result = Math.asin(operand);
                            break;
                        case UnaryMathFunction.ArcCosine:
                            result = Math.acos(operand);
                            break;
                        case UnaryMathFunction.ArcTangent:
                            result = Math.atan(operand);
                            break;
                        case UnaryMathFunction.Logarithm:
                            result = Math.log10(operand);
                            break;
                        default:
                            return undefined;
                    }
                    return cleanNumber(result);
                }
                case FormulaKind.BinaryMath: {
                    const f = formula as BinaryMathFormula;

                    const aValue = compute(f.left);
                    if (isLoadingValue(aValue)) return aValue;

                    const bValue = compute(f.right);
                    if (isLoadingValue(bValue)) return bValue;
                    if (bValue instanceof UndefinedDate) return undefined;
                    const b = asMaybeNumber(bValue);

                    // ##dateMathInNewComputationModel:
                    // FIXME: The formulas don't contain type information, we
                    // only bring it in via the type kind in `GetVariable`
                    // below. The NCM doesn't compile formulas yet, so we're
                    // dispatching on the type dynamically.  This is slow.
                    if (aValue instanceof Date) {
                        switch (f.fn) {
                            case BinaryMathFunction.Add:
                                if (b === undefined) return undefined;
                                return new Date(aValue.getTime() + convertGlideDurationToJS(b));
                            case BinaryMathFunction.Subtract:
                                if (bValue === undefined) return undefined;
                                if (bValue instanceof Date) {
                                    return cleanNumber(convertJSDurationToGlide(aValue.getTime() - bValue.getTime()));
                                } else if (b !== undefined) {
                                    return new Date(aValue.getTime() - convertGlideDurationToJS(b));
                                }
                                return undefined;
                            default:
                                // logError("Invalid math on date", f.fn);
                                return undefined;
                        }
                    }
                    if (aValue instanceof UndefinedDate) {
                        switch (f.fn) {
                            case BinaryMathFunction.Add:
                                return undefinedDate;
                            case BinaryMathFunction.Subtract:
                                return undefined;
                            default:
                                // logError("Invalid math on date", f.fn);
                                return undefined;
                        }
                    }

                    const a = asMaybeNumber(aValue);

                    switch (f.fn) {
                        case BinaryMathFunction.Add:
                            if (a === undefined && b === undefined) return undefined;
                            if (a === undefined) {
                                if (f.right.kind === FormulaKind.Constant) return undefined;
                                return b;
                            }
                            if (b === undefined) {
                                if (f.left.kind === FormulaKind.Constant) return undefined;
                                return a;
                            }
                            return cleanNumber(a + b);
                        case BinaryMathFunction.Subtract:
                            if (a === undefined) return undefined;
                            if (b === undefined) {
                                if (f.left.kind === FormulaKind.Constant) return undefined;
                                return a;
                            }
                            return cleanNumber(a - b);
                    }

                    if (a === undefined || b === undefined) return undefined;

                    let result: number | undefined;
                    switch (f.fn) {
                        case BinaryMathFunction.Multiply:
                            result = a * b;
                            break;
                        case BinaryMathFunction.Divide:
                            result = a / b;
                            break;
                        case BinaryMathFunction.Modulo:
                            result = modulo(a, b);
                            break;
                        case BinaryMathFunction.Minimum:
                            result = Math.min(a, b);
                            break;
                        case BinaryMathFunction.Maximum:
                            result = Math.max(a, b);
                            break;
                        case BinaryMathFunction.Power:
                            result = Math.pow(a, b);
                            break;
                        case BinaryMathFunction.Logarithm:
                            result = log2(a, b);
                            break;
                        case BinaryMathFunction.ArcTangent2:
                            result = Math.atan2(a, b);
                            break;
                        case BinaryMathFunction.Round:
                            result = round2(a, b);
                            break;
                        case BinaryMathFunction.Truncate:
                            result = trunc2(a, b);
                            break;
                        default:
                            return panic("FIXME: implement");
                    }
                    return definedMap(result, cleanNumber);
                }
                case FormulaKind.GetVariable:
                    const f = formula as GetVariableFormula;
                    const kind = this._variableTypes.get(f.name);
                    if (kind === undefined) return undefined;
                    const v = context.lookupVariable(f.name);
                    if (isLoadingValue(v)) return v;
                    if (isDateOrDateTimeTypeKind(kind)) {
                        if (!isPrimitive(v)) return undefinedDate;
                        return this.parseDateTime(v, context.setAllDirty) ?? undefinedDate;
                    } else {
                        return v;
                    }
                default:
                    return panic("FIXME: implement");
            }
        };

        let rootFormula = this._formula;
        if (rootFormula.kind === FormulaKind.WithUserEnteredText) {
            rootFormula = (rootFormula as WithUserEnteredTextFormula).formula;
        }

        // The old computation model just passes through values if the formula
        // is just something like `x`.  It doesn't coerce into number or date,
        // so we don't, either.
        if (rootFormula.kind === FormulaKind.GetVariable) {
            return context.lookupVariable((rootFormula as GetVariableFormula).name);
        }

        const result = compute(rootFormula);
        if (result instanceof UndefinedDate) return undefined;
        return result;
    }
}
